import { useId } from 'react';
import {
  PRELIMINARY_RCA_LIST_FIELD_LIMITS,
  PRELIMINARY_RCA_MAX_CONTRIBUTING_FACTORS,
  PRELIMINARY_RCA_MAX_EVIDENCE_ITEMS,
  PRELIMINARY_RCA_MAX_HYPOTHESES,
  PRELIMINARY_RCA_TEXT_FIELD_LIMITS,
  PRELIMINARY_RCA_TITLE,
  RCA_CONTRIBUTING_FACTOR_CATEGORIES,
  RCA_CONTRIBUTING_FACTOR_CATEGORY_LABELS,
  RCA_SUGGESTED_CATEGORIES,
  RCA_SUGGESTED_CATEGORY_LABELS,
} from '@dha-nexus/shared';

// Preliminary problem analysis review (typed help/intake framework, commit 4).
//
// THREE RULES THIS COMPONENT EXISTS TO HOLD
//
// 1. The heading is the shared constant, verbatim. This screen shows a HYPOTHESIS
//    that needs a human to confirm it; a shorter title ("Problem analysis") would
//    read as a finding, and that is the one impression this vertical must never
//    give.
// 2. Provenance is rendered from the SERVER's object, never composed here. A
//    seeded, illustrative, fallback, or deterministic run says so in its own words
//    below. There is no code path in this file that can print "PRIME analysed this"
//    about a run that did not happen.
// 3. Every section is editable, because the reporter is the authority on their own
//    problem. Nothing is read-only except the derived confidence, which is a
//    display value PRIME computes from the content and deliberately not something
//    a reporter (or a model) can talk up.
//
// Confidence, status, and every other state is conveyed in TEXT plus a glyph, never
// by color alone.

const LIST_FIELDS = Object.freeze([
  {
    field: 'observedSymptoms',
    label: 'What you can see happening',
    hint: 'One symptom per line — what PRIME does, not why.',
    itemNoun: 'symptom',
  },
  {
    field: 'knownFacts',
    label: 'What we know',
    hint: 'Things you are confident about.',
    itemNoun: 'fact',
  },
  {
    field: 'assumptions',
    label: 'What we are assuming',
    hint: 'Anything taken for granted that has not been checked.',
    itemNoun: 'assumption',
  },
  {
    field: 'missingInformation',
    label: 'Still to find out',
    hint: 'Open questions. Answer any you can — it makes the request much easier to act on.',
    itemNoun: 'open question',
  },
  {
    field: 'affectedCapabilities',
    label: 'Parts of PRIME affected',
    hint: 'The screens or capabilities this touches.',
    itemNoun: 'capability',
  },
]);

const CONFIDENCE_COPY = Object.freeze({
  low: {
    glyph: 'i',
    label: 'Low confidence',
    detail: 'There is not enough evidence here yet. Answering the open questions raises it.',
  },
  medium: {
    glyph: 'i',
    label: 'Medium confidence',
    detail: 'Some hypotheses have evidence behind them. Open questions remain.',
  },
  high: {
    glyph: '✓',
    label: 'High confidence',
    detail: 'Several hypotheses have evidence and nothing is outstanding.',
  },
});

/**
 * Honest, reporter-readable provenance.
 *
 * Ordered by how much it should change a reader's trust: a deterministic template
 * first (PRIME wrote nothing), then a seeded or illustrative corpus, then a real
 * run. Every branch names WHICH provider actually answered.
 */
export function describeProvenance({ source, provenance }) {
  const actual = provenance?.actualProvider || 'unknown';
  const requested = provenance?.requestedProvider || null;

  if (source === 'deterministic_skeleton') {
    return {
      glyph: 'i',
      tone: 'tone-info',
      heading: 'PRIME did not analyse this — you are writing it',
      body: 'This started as a template built from your own answers. No AI assistant was involved, and nothing here was generated.',
      disclosures: [],
    };
  }

  // EVERY applicable condition, not the first match. Seeded grounding, a silent
  // fallback, and a partial execution can all be true of the same run, and each is a
  // separate fact about how much to trust it. The earlier version returned from the
  // first matching branch, so a run that was both seeded AND partial disclosed only the
  // seeding and suppressed the partial-execution warning entirely.
  const disclosures = [];
  if (provenance?.fallbackOccurred) {
    disclosures.push(
      `PRIME asked ${requested || 'its AI provider'} and it was unavailable, so a stand-in answered instead (${actual}).`,
    );
  }
  if (provenance?.seeded || provenance?.illustrative) {
    disclosures.push(
      `A model (${actual}) wrote it, but it was grounded in seeded demonstration content rather than the live PRIME knowledge base, so treat the reasoning as a starting point and not as fact.`,
    );
  }
  if (provenance?.partialExecution?.occurred) {
    disclosures.push(
      `Part of the work did not finish — ${PARTIAL_REASON_TEXT[provenance.partialExecution.reason] || 'the run completed only partially'} — so sections may be thin or missing.`,
    );
  }

  if (disclosures.length === 0) {
    return {
      glyph: 'i',
      tone: 'tone-info',
      heading: 'Drafted by PRIME’s AI assistant — a hypothesis, not a finding',
      body: `${actual}${provenance?.modelId ? ` (${provenance.modelId})` : ''} produced this draft from your conversation. PRIME has not confirmed any cause; that is what your confirmation below is for.`,
      disclosures: [],
    };
  }

  // The heading names the most serious condition; the body lists them all.
  const heading = provenance?.fallbackOccurred
    ? 'This came from a stand-in, not live analysis'
    : provenance?.seeded || provenance?.illustrative
      ? 'Drafted against illustrative material, not live PRIME knowledge'
      : 'PRIME only partly completed this analysis';
  return {
    glyph: '!',
    tone: 'tone-warn',
    heading,
    body: 'Read every line before you confirm it. PRIME has kept the confidence low.',
    disclosures,
  };
}

// Reporter-readable renderings of the machine-readable partial-execution reasons. A raw
// code in a banner tells a reporter nothing they can act on.
const PARTIAL_REASON_TEXT = Object.freeze({
  retrieval_empty: 'PRIME found no supporting material to ground it in',
  retrieval_failed: 'PRIME could not reach its reference material',
  model_response_incomplete: 'the model’s answer was cut short',
  provider_timeout: 'the AI provider timed out partway through',
  structured_output_unsupported: 'this environment cannot produce a structured analysis',
  unknown: 'the run completed only partially',
});

function TextField({ field, label, hint, value, rows = 3, required = false, disabled, onChange }) {
  const id = `rca-${field}`;
  const limit = PRELIMINARY_RCA_TEXT_FIELD_LIMITS[field];
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>
        {label}
        {required ? (
          <span className="form-required" aria-hidden>
            {' '}
            *
          </span>
        ) : null}
      </label>
      <div className="form-control">
        <textarea
          className="form-input form-textarea"
          id={id}
          name={field}
          rows={rows}
          value={value || ''}
          maxLength={limit}
          required={required}
          disabled={disabled}
          aria-describedby={`${id}-hint`}
          onChange={(event) => onChange(field, event.target.value)}
        />
      </div>
      <div className="form-hint" id={`${id}-hint`}>
        {hint}{' '}
        <span className="support-ticket-char-count">
          {(value || '').length} of {limit} characters
        </span>
      </div>
    </div>
  );
}

// A list editor rather than a newline-split textarea: the contract stores an array
// with per-item bounds, and splitting text would hide which entry is too long.
function ListEditor({ field, label, hint, itemNoun, entries, disabled, onChange }) {
  const bounds = PRELIMINARY_RCA_LIST_FIELD_LIMITS[field];
  const groupId = `rca-${field}`;
  const atLimit = entries.length >= bounds.maxItems;
  return (
    <fieldset className="rca-list" data-testid={`rca-list-${field}`}>
      <legend>{label}</legend>
      <p className="support-ticket-card-hint" id={`${groupId}-hint`}>
        {hint} At most {bounds.maxItems} entries.
      </p>
      {entries.length === 0 ? (
        <p className="support-ticket-card-hint">Nothing here yet.</p>
      ) : (
        <ul className="rca-list-items">
          {entries.map((entry, index) => (
            <li key={`${field}-${index}`}>
              <label className="rca-visually-hidden" htmlFor={`${groupId}-${index}`}>
                {label}, {itemNoun} {index + 1}
              </label>
              <input
                className="form-input"
                id={`${groupId}-${index}`}
                type="text"
                value={entry}
                maxLength={bounds.maxLength}
                disabled={disabled}
                aria-describedby={`${groupId}-hint`}
                onChange={(event) => {
                  const next = [...entries];
                  next[index] = event.target.value;
                  onChange(field, next);
                }}
              />
              <button
                type="button"
                className="btn ghost sm"
                disabled={disabled}
                onClick={() =>
                  onChange(
                    field,
                    entries.filter((_value, at) => at !== index),
                  )
                }
              >
                Remove
                <span className="rca-visually-hidden">
                  {' '}
                  {itemNoun} {index + 1}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="btn ghost sm"
        data-testid={`rca-add-${field}`}
        disabled={disabled || atLimit}
        onClick={() => onChange(field, [...entries, ''])}
      >
        Add a {itemNoun}
      </button>
      {atLimit ? (
        <p className="support-ticket-card-hint">
          That is the maximum of {bounds.maxItems} entries.
        </p>
      ) : null}
    </fieldset>
  );
}

function ContributingFactors({ entries, disabled, onChange }) {
  const atLimit = entries.length >= PRELIMINARY_RCA_MAX_CONTRIBUTING_FACTORS;
  return (
    <fieldset className="rca-list" data-testid="rca-contributing-factors">
      <legend>What might be contributing</legend>
      <p className="support-ticket-card-hint" id="rca-factors-hint">
        Possible contributors, each with the kind of thing it is. None of these is a confirmed
        cause.
      </p>
      {entries.length === 0 ? (
        <p className="support-ticket-card-hint">Nothing here yet.</p>
      ) : (
        <ul className="rca-list-items">
          {entries.map((entry, index) => (
            <li key={`factor-${index}`} className="rca-factor-row">
              <label className="rca-visually-hidden" htmlFor={`rca-factor-category-${index}`}>
                Contributor {index + 1} category
              </label>
              <div className="form-select-wrap">
                <select
                  className="form-input form-select"
                  id={`rca-factor-category-${index}`}
                  value={entry.category}
                  disabled={disabled}
                  onChange={(event) => {
                    const next = [...entries];
                    next[index] = { ...entry, category: event.target.value };
                    onChange('possibleContributingFactors', next);
                  }}
                >
                  {RCA_CONTRIBUTING_FACTOR_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {RCA_CONTRIBUTING_FACTOR_CATEGORY_LABELS[category]}
                    </option>
                  ))}
                </select>
                <span className="form-select-chevron" aria-hidden>
                  ▾
                </span>
              </div>
              <label className="rca-visually-hidden" htmlFor={`rca-factor-description-${index}`}>
                Contributor {index + 1} description
              </label>
              <input
                className="form-input"
                id={`rca-factor-description-${index}`}
                type="text"
                value={entry.description}
                maxLength={400}
                disabled={disabled}
                aria-describedby="rca-factors-hint"
                onChange={(event) => {
                  const next = [...entries];
                  next[index] = { ...entry, description: event.target.value };
                  onChange('possibleContributingFactors', next);
                }}
              />
              <button
                type="button"
                className="btn ghost sm"
                disabled={disabled}
                onClick={() =>
                  onChange(
                    'possibleContributingFactors',
                    entries.filter((_value, at) => at !== index),
                  )
                }
              >
                Remove<span className="rca-visually-hidden"> contributor {index + 1}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="btn ghost sm"
        data-testid="rca-add-contributing-factor"
        disabled={disabled || atLimit}
        onClick={() =>
          onChange('possibleContributingFactors', [
            ...entries,
            { category: RCA_CONTRIBUTING_FACTOR_CATEGORIES[0], description: '' },
          ])
        }
      >
        Add a contributor
      </button>
    </fieldset>
  );
}

function EvidenceList({ hypothesisIndex, kind, label, entries, disabled, onChange }) {
  const groupId = `rca-hypothesis-${hypothesisIndex}-${kind}`;
  const atLimit = entries.length >= PRELIMINARY_RCA_MAX_EVIDENCE_ITEMS;
  return (
    <div className="rca-evidence">
      <p className="rca-evidence-label" id={`${groupId}-label`}>
        {label}
      </p>
      {entries.length === 0 ? (
        <p className="support-ticket-card-hint">None recorded.</p>
      ) : (
        <ul className="rca-list-items">
          {entries.map((entry, index) => (
            <li key={`${groupId}-${index}`}>
              <label className="rca-visually-hidden" htmlFor={`${groupId}-${index}`}>
                {label} {index + 1}
              </label>
              <input
                className="form-input"
                id={`${groupId}-${index}`}
                type="text"
                value={entry}
                maxLength={300}
                disabled={disabled}
                onChange={(event) => {
                  const next = [...entries];
                  next[index] = event.target.value;
                  onChange(next);
                }}
              />
              <button
                type="button"
                className="btn ghost sm"
                disabled={disabled}
                onClick={() => onChange(entries.filter((_value, at) => at !== index))}
              >
                Remove
                <span className="rca-visually-hidden">
                  {' '}
                  {label} {index + 1}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="btn ghost sm"
        disabled={disabled || atLimit}
        onClick={() => onChange([...entries, ''])}
      >
        Add {label.toLowerCase()}
      </button>
    </div>
  );
}

function Hypotheses({ entries, disabled, onChange }) {
  const atLimit = entries.length >= PRELIMINARY_RCA_MAX_HYPOTHESES;
  function update(index, patch) {
    const next = [...entries];
    next[index] = { ...next[index], ...patch };
    onChange('hypotheses', next);
  }
  return (
    <section className="rca-hypotheses" aria-labelledby="rca-hypotheses-heading">
      <h3 id="rca-hypotheses-heading">Possible explanations</h3>
      <p className="support-ticket-card-hint">
        Each of these is a guess PRIME or you wrote down, with whatever supports or contradicts it.
        Remove any that are wrong — that is useful information too.
      </p>
      {entries.length === 0 ? (
        <p className="support-ticket-card-hint" data-testid="rca-hypotheses-empty">
          No explanations recorded yet.
        </p>
      ) : null}
      {entries.map((entry, index) => (
        <article
          className="rca-hypothesis"
          key={entry.id || `hypothesis-${index}`}
          data-testid="rca-hypothesis"
          aria-labelledby={`rca-hypothesis-${index}-heading`}
        >
          <header>
            <h4 id={`rca-hypothesis-${index}-heading`}>Explanation {index + 1}</h4>
            <button
              type="button"
              className="btn ghost sm"
              data-testid={`rca-remove-hypothesis-${index}`}
              disabled={disabled}
              onClick={() =>
                onChange(
                  'hypotheses',
                  entries.filter((_value, at) => at !== index),
                )
              }
            >
              Remove<span className="rca-visually-hidden"> explanation {index + 1}</span>
            </button>
          </header>
          <div className="form-field">
            <label className="form-label" htmlFor={`rca-hypothesis-${index}-statement`}>
              What might be going on
            </label>
            <div className="form-control">
              <textarea
                className="form-input form-textarea"
                id={`rca-hypothesis-${index}-statement`}
                rows={2}
                value={entry.statement || ''}
                maxLength={400}
                disabled={disabled}
                onChange={(event) => update(index, { statement: event.target.value })}
              />
            </div>
          </div>
          <div className="rca-evidence-pair">
            <EvidenceList
              hypothesisIndex={index}
              kind="supporting"
              label="Supporting evidence"
              entries={entry.supportingEvidence || []}
              disabled={disabled}
              onChange={(next) => update(index, { supportingEvidence: next })}
            />
            <EvidenceList
              hypothesisIndex={index}
              kind="contradicting"
              label="Evidence against"
              entries={entry.contradictingEvidence || []}
              disabled={disabled}
              onChange={(next) => update(index, { contradictingEvidence: next })}
            />
          </div>
          {entry.sourceTurnRefs?.length ? (
            <p className="support-ticket-card-hint">
              Drawn from {entry.sourceTurnRefs.length} turn
              {entry.sourceTurnRefs.length === 1 ? '' : 's'} of your conversation.
            </p>
          ) : null}
        </article>
      ))}
      <button
        type="button"
        className="btn ghost sm"
        data-testid="rca-add-hypothesis"
        disabled={disabled || atLimit}
        onClick={() =>
          onChange('hypotheses', [
            ...entries,
            {
              statement: '',
              supportingEvidence: [],
              contradictingEvidence: [],
              sourceTurnRefs: [],
            },
          ])
        }
      >
        Add an explanation
      </button>
    </section>
  );
}

export function RcaReviewSection({
  rca,
  content,
  disabled = false,
  acknowledged,
  onAcknowledgeChange,
  onChange,
  onApprove,
  onBack,
  onSave = null,
  busy = false,
  saveState = 'idle',
  dirty = false,
  validationKeys = [],
}) {
  const acknowledgeId = useId();
  const provenanceCopy = describeProvenance({ source: rca?.source, provenance: rca?.provenance });
  const confidence = CONFIDENCE_COPY[content?.confidence] || CONFIDENCE_COPY.low;
  // A stored approval is only meaningful for the text the reporter actually
  // confirmed. The moment the on-screen analysis diverges from it — an edit made and
  // not yet persisted — this screen must stop saying "confirmed" and ask again.
  // Presenting an unsaved edit as confirmed is precisely how a reporter ends up
  // believing they approved text nobody ever read.
  const approved = Boolean(rca?.approvedAt) && !dirty;

  return (
    <section className="rca-review" aria-labelledby="rca-heading" data-testid="rca-review">
      {/* The shared constant, verbatim. This is a hypothesis awaiting confirmation,
          and the heading has to say so. */}
      <h2 id="rca-heading">{PRELIMINARY_RCA_TITLE}</h2>
      <p className="support-ticket-card-hint">
        PRIME has not confirmed anything below. Read it, correct anything that is wrong, answer what
        you can, then confirm it so the product team knows it matches what you meant.
      </p>

      <div
        className={`support-ticket-alert ${provenanceCopy.tone}`}
        role="note"
        data-testid="rca-provenance"
      >
        {/* The tone glyph comes from the tone class in feedbackIntake.css
            (`.support-ticket-alert.tone-info h3::before` and its siblings). A local
            `<span aria-hidden>{glyph}</span>` here printed it a SECOND time — the
            banner read "i i Drafted by PRIME's AI assistant" on screen (typed-intake
            commit 7 visual audit). `describeProvenance().glyph` and the tone class
            agree by construction, so the class is enough. */}
        <h3>{provenanceCopy.heading}</h3>
        <p>{provenanceCopy.body}</p>
        {/* Every applicable disclosure, listed. A run can be seeded AND partial AND a
            fallback, and each is a separate reason to trust it less. */}
        {provenanceCopy.disclosures?.length ? (
          <ul data-testid="rca-provenance-disclosures">
            {provenanceCopy.disclosures.map((disclosure) => (
              <li key={disclosure}>{disclosure}</li>
            ))}
          </ul>
        ) : null}
        {rca?.userEdited ? <p>You have edited this analysis.</p> : null}
      </div>

      {validationKeys.length ? (
        <div className="support-ticket-alert tone-danger" role="alert" data-testid="rca-validation">
          <h3>Finish these before confirming</h3>
          <ul>
            {validationKeys.includes('preliminaryRca.problemStatement') ? (
              <li>
                <a href="#rca-problemStatement">The problem statement</a> — this is required.
              </li>
            ) : null}
            {validationKeys.includes('preliminaryRca.desiredOutcome') ? (
              <li>
                <a href="#rca-desiredOutcome">What you want to be able to do</a> — this is required.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <div className="support-ticket-card">
        <h3>The problem, in one paragraph</h3>
        <TextField
          field="problemStatement"
          label="Problem statement"
          hint="What is going wrong, or what is missing."
          value={content?.problemStatement}
          rows={4}
          required
          disabled={disabled}
          onChange={onChange}
        />
        <TextField
          field="desiredOutcome"
          label="What you want to be able to do"
          hint="Describe the outcome, not a solution design."
          value={content?.desiredOutcome}
          rows={3}
          required
          disabled={disabled}
          onChange={onChange}
        />
      </div>

      <div className="support-ticket-card">
        <h3>What we know so far</h3>
        {LIST_FIELDS.slice(0, 4).map((entry) => (
          <ListEditor
            key={entry.field}
            {...entry}
            entries={content?.[entry.field] || []}
            disabled={disabled}
            onChange={onChange}
          />
        ))}
      </div>

      <div className="support-ticket-card">
        <ContributingFactors
          entries={content?.possibleContributingFactors || []}
          disabled={disabled}
          onChange={onChange}
        />
      </div>

      <div className="support-ticket-card">
        <Hypotheses entries={content?.hypotheses || []} disabled={disabled} onChange={onChange} />
      </div>

      <div className="support-ticket-card">
        <h3>Impact and what happens next</h3>
        <TextField
          field="currentWorkaround"
          label="What you do instead today"
          hint="Your workaround, if you have one."
          value={content?.currentWorkaround}
          disabled={disabled}
          onChange={onChange}
        />
        <TextField
          field="impact"
          label="What this costs"
          hint="Time, rework, risk, or a missed deadline."
          value={content?.impact}
          disabled={disabled}
          onChange={onChange}
        />
        <ListEditor
          {...LIST_FIELDS[4]}
          entries={content?.affectedCapabilities || []}
          disabled={disabled}
          onChange={onChange}
        />
        <div className="form-field">
          <label className="form-label" htmlFor="rca-suggestedCategory">
            What kind of request this is
          </label>
          <div className="form-control">
            <div className="form-select-wrap">
              <select
                className="form-input form-select"
                id="rca-suggestedCategory"
                value={content?.suggestedCategory || ''}
                disabled={disabled}
                aria-describedby="rca-suggestedCategory-hint"
                onChange={(event) => onChange('suggestedCategory', event.target.value || null)}
              >
                <option value="">Not sure yet</option>
                {RCA_SUGGESTED_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {RCA_SUGGESTED_CATEGORY_LABELS[category]}
                  </option>
                ))}
              </select>
              <span className="form-select-chevron" aria-hidden>
                ▾
              </span>
            </div>
          </div>
          <div className="form-hint" id="rca-suggestedCategory-hint">
            A hint for whoever picks this up. PRIME still files the request as an enhancement in the
            governed queue.
          </div>
        </div>
        <TextField
          field="recommendedNextAction"
          label="A sensible next step"
          hint="What would move this forward."
          value={content?.recommendedNextAction}
          disabled={disabled}
          onChange={onChange}
        />
      </div>

      {/* Derived, not editable, and not something a model gets to assert. */}
      <p
        className="support-ticket-delivery-state"
        data-glyph={confidence.glyph}
        data-testid="rca-confidence"
      >
        {confidence.label} — {confidence.detail}
      </p>

      <div className="support-ticket-card support-ticket-no-print">
        {approved ? (
          <p className="support-ticket-delivery-state" data-glyph="✓" data-testid="rca-approved">
            You confirmed this analysis. Editing it again will ask you to confirm the new version.
          </p>
        ) : (
          <>
            {rca?.approvedAt && dirty ? (
              <p
                className="support-ticket-delivery-state"
                data-glyph="!"
                data-testid="rca-reconfirm-needed"
              >
                You changed the analysis after confirming it. Save your changes and confirm the new
                version before sending.
              </p>
            ) : null}
            <label className="support-ticket-check is-required">
              <input
                type="checkbox"
                id={acknowledgeId}
                name="rcaAcknowledged"
                checked={acknowledged}
                disabled={disabled}
                onChange={(event) => onAcknowledgeChange(event.target.checked)}
              />
              <span>
                I have read this preliminary analysis and it matches the problem I am reporting. I
                understand PRIME has not confirmed a cause.
              </span>
            </label>
            <div className="support-ticket-actions">
              <button
                type="button"
                className="btn primary"
                data-testid="rca-approve"
                disabled={busy || disabled || !acknowledged}
                onClick={onApprove}
              >
                {busy ? 'Confirming…' : 'Confirm this analysis'}
              </button>
              {!acknowledged ? (
                <span className="support-ticket-action-note">Tick the box above to confirm.</span>
              ) : null}
            </div>
          </>
        )}
        {/* Navigation and an explicit draft save are available in BOTH states.
            They were previously trapped inside the un-approved branch, which left a
            confirmed analysis with no way back to the conversation and no way to
            flush an edit without triggering a governance action. */}
        <div className="support-ticket-actions">
          {onSave ? (
            <button
              type="button"
              className="btn ghost"
              data-testid="rca-save"
              disabled={busy || saveState === 'saving'}
              onClick={onSave}
            >
              Save draft
            </button>
          ) : null}
          <button
            type="button"
            className="btn ghost"
            data-testid="rca-back"
            disabled={busy}
            onClick={onBack}
          >
            Back to the conversation
          </button>
        </div>
      </div>
    </section>
  );
}

export default RcaReviewSection;
